import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import React, { useEffect, useState } from 'react';
import { LocalStorageKey } from '../../../constants/localStorageKey';
import { useLocalStorage } from '../../../hooks';
import { Typography } from '../../atoms';
import { StyledTextField } from './Search.styles';

export type SearchProps<T> = {
  fieldLabel: string;
  options: T[];
  disabled?: boolean;
  clearable?: boolean;
  markerOption?: T | null;
  getOptionLabel: (option: T) => string;
  onSelect: (value: T | null) => void;
  filterFunction?: (options: T[], inputString: string) => T[];
  onClear?: () => void;
  inputFieldSize?: 'small' | 'medium';
};

export const Search = <T,>({
  fieldLabel,
  options,
  disabled = false,
  clearable = true,
  markerOption = null,
  onSelect,
  getOptionLabel,
  filterFunction,
  onClear,
  inputFieldSize = 'medium',
}: SearchProps<T>) => {
  const { removeItem } = useLocalStorage();
  const [inputValue, setInputValue] = useState('');
  const [dropDownValue, setDropDownValue] = useState<T | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleOnChange = (_: React.SyntheticEvent<Element, Event>, value: T | null) => {
    if (value === null) {
      removeItem(LocalStorageKey.SelectedView);
      if (onClear) onClear();
    }

    setDropDownValue(value);
    setInputValue('');
    onSelect(value);
  };

  useEffect(() => {
    setDropDownValue(markerOption);
    if (!markerOption) {
      setInputValue('');
    }
  }, [markerOption]);

  return (
    <Autocomplete
      data-testid="search-container"
      filterOptions={filterFunction ? (_options, _) => filterFunction(_options, inputValue) : undefined}
      options={options}
      getOptionLabel={getOptionLabel}
      renderInput={(params) => (
        <StyledTextField
          data-testid="search-text-field"
          {...params}
          size={inputFieldSize}
          value={inputValue}
          label={<Typography>{fieldLabel}</Typography>}
          variant="outlined"
          onChange={handleInputChange}
        />
      )}
      value={dropDownValue}
      renderOption={(props, option) => (
        <li {...props}>
          <Typography>{getOptionLabel(option)}</Typography>
        </li>
      )}
      renderTags={(value: T[], getTagProps) =>
        value.map((option, index) => <Typography {...getTagProps({ index })}>{getOptionLabel(option)}</Typography>)
      }
      onChange={handleOnChange}
      PaperComponent={(props) => <Paper {...props} elevation={3} />}
      onBlur={() => setInputValue('')}
      disabled={disabled}
      disableClearable={!clearable}
    />
  );
};
